import {OrderLifetime} from "../../portfolio/templates/ticket/place-equity-order-wizard/models/EquityOrder";
import {IsoDate, IsoDateTime} from "../../pseudo-types/DateTypes";
import {CodeAndExchange} from "app/instruments/types/CodeAndExchange";
import {PortfolioType} from "../../portfolio/models/Portfolio";
import {Exchange} from "../../instruments/exchange/Exchange";
import {OrderRecord} from "./OrderRecord";

export interface OrderResponse {
    readonly orders: Orders;
}

export interface BaseOrder extends CodeAndExchange {
    readonly quantity: number;
    readonly limit: number;
    readonly description: string;
    readonly buy: boolean;
}

export type Orders = ReadonlyArray<Order>;

export interface Order extends BaseOrder {
    readonly id: number;
    readonly portfolioId: number;
    readonly portfolioType: PortfolioType;
    readonly accountNumber: string;
    readonly accountName: string;
    readonly orderNumber: string;
    readonly externalRef: string;
    readonly created: IsoDateTime;
    readonly updated: IsoDateTime;
    readonly completed: IsoDateTime | null;
    readonly status: OrderStatus;
    readonly lastAction: LastAction;
    readonly lastActionStatus: LastActionStatus;
    readonly closedReason: CloseReason | null;
    readonly priceInstruction: string;
    readonly side: OrderSide;
    readonly lifetime: OrderLifetime;
    readonly expiryDate: IsoDateTime | null;
    readonly underlying: string;
    readonly filledQuantity: number;
    readonly filledPrice: number | null;
    readonly changeSeq: string | null;
    readonly createdBy: CreatedBy;
    readonly tmc: boolean;
    readonly cvf: number;
}

export interface TmcOrder extends Order {
    readonly tmc: true;
    readonly opposite: boolean;
    readonly legs: ReadonlyArray<OrderLeg>;
}

export interface SingleOrder extends Order {
    readonly tmc: false;
}

export type EquityOrder = SingleOrder;
export type WarrantOrder = SingleOrder;

export interface OptionOrderProperties {
    readonly style: ExerciseStyle;
    readonly type: OptionType;
    readonly expiry: IsoDate;
    readonly strike: number;
    readonly daysToExpiry: number;
}

export type OptionOrder = SingleOrder & OptionOrderProperties;

export type LegType = "O" | "E";

export interface OrderLeg extends CodeAndExchange {
    readonly ratio: number;
    readonly side: OrderSide;
    readonly legType: LegType;
}

export interface OptionOrderLeg extends OrderLeg, OptionOrderProperties {
    readonly legType: "O";
}

export interface EquityOrderLeg extends OrderLeg {
    readonly legType: "E";
    readonly cvf: number;
}

export interface CreatedBy {
    readonly id: number;
    readonly username: string;
    readonly displayName: string;
}

export enum OrderStatus {
    PENDING = "PENDING",
    WAITING_OPERATOR = "WAITING_OPERATOR",
    ON_MARKET = "ON_MARKET",
    DONE = "DONE"
}

export enum CloseReason {
    FAILED = "FAILED",
    EXPIRED = "EXPIRED",
    FILLED = "FILLED",
    CANCELLED = "CANCELLED",
    REJECTED = "REJECTED"
}

export enum OrderSide {
    BUY = "BUY",
    SELL = "SELL"
}

export enum OptionType {
    PUT = "PUT",
    CALL = "CALL"
}

export enum ExerciseStyle {
    AMERICAN = "AMERICAN",
    EUROPEAN = "EUROPEAN",
    FUTURE = "FUTURE"
}

export enum LastAction {
    CREATE = "CREATE",
    CANCEL = "CANCEL",
    AMEND = "AMEND",
    PURGE = "PURGE"
}

export enum LastActionStatus {
    OK = "OK",
    PENDING = "PENDING",
    AUTHORISING = "AUTHORISING",
    FAILED = "FAILED",
    DENIED = "DENIED"
}

export function isTmcOrder(order: Order): order is TmcOrder {
    return order.tmc;
}

export function isSingleOrder(order: Order): order is SingleOrder {
    return !order.tmc;
}

export function isOptionOrder(order: Order): order is OptionOrder {
    return order.exchange === Exchange.AOM && isSingleOrder(order);
}

export function isEquityOrder(order: Order): order is EquityOrder {
    return order.exchange === Exchange.ASX && isSingleOrder(order) && !isOptionOrder(order);
}

export function isWarrantOrder(order: Order): order is WarrantOrder {
    if (order.exchange === Exchange.AXW) {
        return isSingleOrder(order) && !isOptionOrder(order);
    } else if (order.exchange === Exchange.CXA) {
        return order.code.length === 6; // TODO Not currect, temp code
    } else {
        return false;
    }
}

export function isOptionLeg(leg: OrderLeg): leg is OptionOrderLeg {
    return leg.legType === "O";
}

export function isEquityLeg(leg: OrderLeg): leg is EquityOrderLeg {
    return leg.legType === "E";
}

export function isOpen(order: OrderRecord): boolean {
    const openStates = [OrderStatus.PENDING, OrderStatus.WAITING_OPERATOR, OrderStatus.ON_MARKET];
    return openStates.includes(order.status);
}
